import {Justify, Align, PositionType} from '@wonderlandengine/react-ui';
import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {ColoredImage} from '../components/colored-image.js';
import {MalUI} from '../components/mal-ui.js';
import {MenuButton} from '../components/menu-button.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {use, useContext, useEffect, useMemo, useState} from 'react';
import {
    FlatOpaqueTextured,
    UIColorTextured,
    UIColorTexturedAlpha,
} from '../../types/materials.js';
import {
    useRotBMaterialContext,
    RotBMaterialContextValue,
} from '../utils/rotb-material-context.js';
import {insertNewlines, parseColor, setAlpha} from '../utils/utils.js';
import {WindowHeader} from '../components/window-header.js';
import {Window} from '../components/window.js';
import {NotificationModel} from '../../models/NotificationParams.js';
import {Header} from '../components/header.js';
import {Localization} from '../../utils/Localization.js';

import {Component, Object3D, Texture} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';

/**
 * unlock-icons
 *
 * These icons map to the texture names in Unity, that's why they
 * have underscores, inconsistand naming and such.
 */
export class UnlockIcons extends Component {
    static TypeName = 'unlock-icons';

    // Singleton
    private static _instance: UnlockIcons;
    static get instance(): UnlockIcons {
        return UnlockIcons._instance;
    }

    @property.texture()
    fallback: Texture;

    @property.texture()
    button_fruitsmasher: Texture;

    @property.texture()
    buttonBuyBomb: Texture;

    @property.texture()
    icon_normal: Texture;

    @property.texture()
    icon_cupcake: Texture;

    @property.texture()
    grad: Texture;

    init() {
        if (UnlockIcons._instance) {
            console.error('There can only be one instance of UnlockIcons Component');
        }
        UnlockIcons._instance = this;
    }

    getIcon(icon: string) {
        if (!(icon in this)) {
            console.error(`Icon ${icon} does not exist on UnlockIcons component`);
            return this.fallback;
        }
        return this[icon];
    }
}

export const UnlockedSomething = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    let material = rotbMats?.windowMaterial;

    const bm = useMemo(() => material!.clone(), []);
    (bm as UIColorTextured).setColor(parseColor(colorSwatch.Window));

    const darkBackgroundMaterial = useMemo(() => {
        const m = rotbMats.gradientMaterial!.clone() as UIColorTexturedAlpha;
        m.setColor(parseColor('#000000A0'));
        m.setFlatTexture(UiTexturesManager.instance.whiteSquare);
        return m;
    }, []);

    const shadowMaterial = rotbMats?.windowShadowMaterial;
    const gradientMaterial = rotbMats?.gradientMaterial;
    (gradientMaterial as any).setColor(parseColor(colorSwatch.WindowGradient));

    const ground = useMemo(() => {
        const m = rotbMats?.gradientMaterial.clone() as FlatOpaqueTextured;
        m.setColor(parseColor(colorSwatch.SliderGround));
        return m;
    }, []);

    const [notificationParams, setNotificationParams] = useState(
        NotificationModel.instance.params
    );

    useEffect(() => {
        const update = () => {
            setNotificationParams(NotificationModel.instance.params);
        };
        NotificationModel.instance.propertyChanged.add(update);
        return () => {
            NotificationModel.instance.propertyChanged.remove(update);
        };
    });

    return (
        <Panel
            rounding={0}
            width="100%"
            height="100%"
            justifyContent={Justify.Center}
            alignItems={Align.Center}
            material={darkBackgroundMaterial}
        >
            <Panel9Slice
                width={480}
                height={550}
                borderSize={5}
                material={shadowMaterial}
                borderTextureSize={9 / 64}
            >
                <Panel9Slice
                    left={-4}
                    top={-4}
                    width={480}
                    height={550}
                    borderSize={5}
                    material={bm}
                    borderTextureSize={9 / 64}
                    padding={5}
                >
                    <Panel
                        width="100%"
                        height="100%"
                        rounding={0}
                        material={gradientMaterial}
                    >
                        <Column
                            height="100%"
                            width={400}
                            margin={20}
                            alignSelf={Align.Center}
                            rowGap={20}
                        >
                            <WindowHeader title={notificationParams.title} />
                            <Panel
                                width={400}
                                height={200}
                                rounding={0}
                                backgroundColor={colorSwatch.SliderBackground}
                            >
                                <Panel
                                    material={ground}
                                    width={400}
                                    top={100}
                                    height={100}
                                    rounding={0}
                                    border={0}
                                ></Panel>
                                {notificationParams.iconEnabled && (
                                    <Container
                                        top={-64}
                                        width="100%"
                                        height={200}
                                        alignItems={Align.Center}
                                        z={0.01}
                                    >
                                        <Image
                                            rounding={0}
                                            width={128}
                                            height={128}
                                            src={UnlockIcons.instance.getIcon(
                                                notificationParams.iconSpriteName
                                            )}
                                        ></Image>
                                    </Container>
                                )}
                                {notificationParams.iconModeLevelEnabled && (
                                    <Container
                                        top={-64}
                                        width="100%"
                                        height={200}
                                        alignItems={Align.Center}
                                        z={0.01}
                                    >
                                        <Image
                                            rounding={0}
                                            width={128}
                                            height={128}
                                            src={UnlockIcons.instance.getIcon(
                                                notificationParams.iconModeLevel
                                            )}
                                        ></Image>
                                    </Container>
                                )}
                                {notificationParams.iconMalwearEnabled && (
                                    <Container
                                        top={-85}
                                        width="100%"
                                        height={200}
                                        alignItems={Align.Center}
                                        z={0.01}
                                        key="iconMalwearEnabled"
                                    >
                                        <Image
                                            rounding={0}
                                            width={256}
                                            height={174}
                                            src={UnlockIcons.instance.getIcon(
                                                notificationParams.iconMalwearSpriteName
                                            )}
                                        ></Image>
                                    </Container>
                                )}
                            </Panel>
                            <Container flexGrow={1}>
                                <Header
                                    title={notificationParams.popUpDescription}
                                    color={colorSwatch.SubTitleHighBackground}
                                    center={true}
                                />
                                <Container
                                    height={75}
                                    alignContent={Align.FlexStart}
                                    padding={10}
                                >
                                    <Text
                                        color={setAlpha(colorSwatch.DescriptionText, '90%')}
                                        material={rotbMats.regularTextMaterial}
                                        fontSize={20}
                                        textAlign="center"
                                    >
                                        {insertNewlines(
                                            notificationParams.popUpDescriptionDetail,
                                            40
                                        )}
                                    </Text>
                                </Container>
                            </Container>
                            <Column
                                width="100%"
                                gap={20}
                                justifyContent={Justify.Center}
                                alignContent={Align.Center}
                                alignItems={Align.Center}
                                marginBottom={40}
                            >
                                {notificationParams.showSwitchNowObject && (
                                    <MenuButton
                                        width={250}
                                        height={54}
                                        margin={6}
                                        color={colorSwatch.SpendCoinsButton}
                                        fontSize={32}
                                        onClick={() => notificationParams.message.onClick()}
                                        text={Localization.instance.get('TryItOut')}
                                    ></MenuButton>
                                )}
                                {notificationParams.showEquipObject && (
                                    <MenuButton
                                        width={250}
                                        height={54}
                                        margin={6}
                                        color={colorSwatch.PowerupLocked}
                                        fontSize={32}
                                        onClick={() => notificationParams.equip.onClick()}
                                        text={Localization.instance.get('Equip')}
                                    ></MenuButton>
                                )}
                                {!notificationParams.showEquipObject &&
                                    !notificationParams.showSwitchNowObject && (
                                        <MenuButton
                                            width={250}
                                            height={54}
                                            margin={6}
                                            color={colorSwatch.AcceptButton}
                                            fontSize={32}
                                            onClick={() =>
                                                notificationParams.message.onClick()
                                            }
                                            text={Localization.instance.get(
                                                'PopUpNotificationAccept'
                                            )}
                                        ></MenuButton>
                                    )}
                            </Column>
                        </Column>
                    </Panel>
                </Panel9Slice>
            </Panel9Slice>
        </Panel>
    );
};
